import { Form, Select, Button, Breadcrumb, Card, Modal, Space, Typography } from "antd";
import { useLoaderData, useNavigate } from "react-router-dom";
import * as APITypes from "~/API";
import { PaymentPageLoaderData } from "./loaders/PaymentPageLoader";
import { useMakePaymentForm } from "./hooks";
import { UploadFileButton } from "../MyDocumentPage/components";
import { auth } from "~/lib/firebase";
import { CustomTextField } from "~/components";
import { HomeOutlined } from "@ant-design/icons";
import {
  ROUTES,
  formatRentPaymentPeriodLabel,
  isPartialRentCoveragePeriod,
} from "~/utils";
import { useAppTranslation } from "~/hooks/useAppTranslation";

export const PaymentPage = () => {
  const loaderData = useLoaderData() as PaymentPageLoaderData;
  const { t, i18n } = useAppTranslation();
  const currentUser = auth.currentUser;
  const navigate = useNavigate();

  const usePayment = useMakePaymentForm({
    reason: loaderData.reason,
    lease: loaderData.lease,
    offer: loaderData.offer,
    serviceRequest: loaderData.serviceRequests,
    transactions: loaderData.transactions,
    history: loaderData.history,
    amount: 0,
  });

  const selectedPeriodKey = Form.useWatch(
    "monthlyReport",
    usePayment.formInstance,
  );
  const selectedHistory =
    typeof selectedPeriodKey === "string"
      ? loaderData.history?.find((item) => {
          const [monthStr, yearStr] = selectedPeriodKey.split("-");
          return (
            item.month === parseInt(monthStr, 10) &&
            item.year === parseInt(yearStr, 10)
          );
        })
      : undefined;
  const showProrateHint =
    selectedHistory != null &&
    isPartialRentCoveragePeriod(
      selectedHistory.periodStart,
      selectedHistory.periodEnd,
    );

  return (
    <>
      <Breadcrumb
        separator=">"
        items={[
          { title: <HomeOutlined />, href: ROUTES.home },
          { title: t("Payments") },
        ]}
      />

      <br />

      <Card style={{ width: "100%" }}>
        <Form form={usePayment.formInstance} onFinish={usePayment.handleSubmit}>
          <Form.Item name="reason" label={t("Reason_For_Payment")}>
            <Select
              options={[
                {
                  key: "rent",
                  value: APITypes.TransactionReason.Rent,
                  label: t(APITypes.TransactionReason.Rent),
                },
                {
                  key: "utility",
                  value: "utility",
                  label: t(APITypes.TransactionReason.UtilityOther),
                },
                // TODO: Add other reasons
                // { key: "deposit", value: APITypes.TransactionReason.Deposit },
                // { key: "fee", value: APITypes.TransactionReason.Fee },
                // {
                //   key: "other",
                //   value: APITypes.TransactionReason.OtherPayment,
                // },
              ]}
              onChange={(value) => usePayment.handleReasonChange(value)}
              allowClear
            />
          </Form.Item>

          {loaderData.reason === APITypes.TransactionReason.Rent && (
            <Form.Item name="monthlyReport" label={t("Period")}>
              <Select
                options={loaderData.history?.map((month) => ({
                  key: `${month.month}-${month.year}`,
                  value: `${month.month}-${month.year}`,
                  label: formatRentPaymentPeriodLabel({
                    month: month.month,
                    year: month.year,
                    periodStart: month.periodStart,
                    periodEnd: month.periodEnd,
                    monthWord: t("Month"),
                    yearWord: t("Year"),
                    appLanguage: i18n.language,
                  }),
                }))}
                onChange={(value) =>
                  usePayment.handleMonthChange(String(value))
                }
              />
            </Form.Item>
          )}

          {showProrateHint && (
            <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
              {t(
                "Rent_period_prorated_hint",
                "This period covers a partial month; the amount is prorated for those days.",
              )}
            </Typography.Paragraph>
          )}

          {loaderData.reason === APITypes.TransactionReason.Fee && (
            <Form.Item name="serviceRequest" label={t("Service_Requests")}>
              <Select
                options={loaderData.serviceRequests?.map((request) => ({
                  key: request.id,
                  value: request.id,
                  label: request.title,
                }))}
              />
            </Form.Item>
          )}

          {loaderData.reason?.toLocaleLowerCase().includes("utility") && (
            <Form.Item name="utility" label={t("All_Transaction_Info")}>
              <Select
                options={loaderData.transactions?.map((transaction) => ({
                  key: transaction.id,
                  value: transaction.id,
                  label: `Utility: ${transaction.reason} Month: ${
                    transaction.month + 1
                  } Year: ${transaction.year}`,
                }))}
                onChange={(value: string) =>
                  usePayment.handleUtilityChange(value)
                }
              />
            </Form.Item>
          )}

          <Form.Item name="amount" label={t("Amount")}>
            <CustomTextField value={usePayment.getAmount().toString()} />
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            disabled={usePayment.isLoading}
          >
            {t("Submit")}
          </Button>
        </Form>
      </Card>

      <Modal
        open={loaderData.transaction_id !== ""}
        onCancel={() => navigate(ROUTES.paymentsPage)}
        footer={null}
      >
        <p>Transaction ID: {loaderData.transaction_id}</p>

        <UploadFileButton
          currentUser={currentUser?.uid ?? ""}
          entityId={loaderData.transaction_id}
          entityType={APITypes.EntityType.Transaction}
        />

        <Space>
          <Button onClick={() => navigate(ROUTES.paymentsPage)}>
            {t("Complete")}
          </Button>
        </Space>
      </Modal>
    </>
  );
};
